import i18n, { intlLocale } from "../i18n";

/** A date in the current UI language, with any Intl options. */
export function formatWith(date: Date, options?: Intl.DateTimeFormatOptions): string {
  return date.toLocaleDateString(intlLocale(), options);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  // A plain day ("2018-03-14") is a calendar date, not UTC midnight: read it as local,
  // or west of Greenwich it shows the day before.
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const date = day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : new Date(iso);
  return isNaN(date.getTime()) ? "—" : formatWith(date, { day: "numeric", month: "long", year: "numeric" });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return isNaN(date.getTime()) ? "—" : date.toLocaleString(intlLocale(), { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** "12/03" (fr) or "03/12" (en-US) for a plain `YYYY-MM-DD` day. */
/** Hours and minutes only ("14:30"), for something happening today. */
export function formatTime(iso: string): string {
  const date = new Date(iso);
  return isNaN(date.getTime()) ? "—" : date.toLocaleTimeString(intlLocale(), { hour: "2-digit", minute: "2-digit" });
}

export function formatShortDay(iso: string): string {
  return formatWith(new Date(iso + "T00:00:00"), { day: "2-digit", month: "2-digit" });
}

/** "aujourd'hui", "il y a 3 jours", "il y a 2 mois"; "—" when there is no date. */
export function timeAgo(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "—";

  const relative = new Intl.RelativeTimeFormat(intlLocale(), { numeric: "auto" });
  const days = Math.round((date.getTime() - now.getTime()) / 86_400_000);
  if (Math.abs(days) < 1) {
    const hours = Math.round((date.getTime() - now.getTime()) / 3_600_000);
    return Math.abs(hours) < 1 ? i18n.t("time.justNow") : relative.format(hours, "hour");
  }
  if (Math.abs(days) < 30) return relative.format(days, "day");
  if (Math.abs(days) < 365) return relative.format(Math.round(days / 30), "month");
  return relative.format(Math.round(days / 365), "year");
}
